import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import UserAvatar from "../components/UserAvatar";
import BlogCommentItem from "../components/blog/BlogCommentItem";
import { useNotification } from "../notifications/NotificationContext";
import { resolveMediaUrl } from "../utils/mediaUrl";
import NotFoundPage from "./NotFoundPage";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

export default function BlogArticlePage() {
    const { slug } = useParams();
    const { isAuthenticated } = useAuth();
    const { showNotification } = useNotification();
    const [article, setArticle] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [comments, setComments] = useState([]);
    const [commentsLoading, setCommentsLoading] = useState(true);
    const [commentText, setCommentText] = useState("");
    const [commentSubmitting, setCommentSubmitting] = useState(false);
    const [commentError, setCommentError] = useState("");

    const loadArticle = useCallback(() => {
        setLoading(true);
        setNotFound(false);
        setMessage("");
        axios
            .get(`${API_BASE_URL}/api/articles/${slug}`, { params: { countView: true } })
            .then((response) => setArticle(response.data || null))
            .catch((err) => {
                if (err?.response?.status === 404) {
                    setNotFound(true);
                    setArticle(null);
                    return;
                }
                setArticle(null);
                setMessage("Не удалось загрузить статью.");
            })
            .finally(() => setLoading(false));
    }, [slug]);

    useEffect(() => {
        loadArticle();
    }, [loadArticle]);

    const loadComments = useCallback(() => {
        setCommentsLoading(true);
        axios
            .get(`${API_BASE_URL}/api/articles/${slug}/comments`)
            .then((response) => setComments(Array.isArray(response.data) ? response.data : []))
            .catch(() => setComments([]))
            .finally(() => setCommentsLoading(false));
    }, [slug]);

    useEffect(() => {
        if (!slug) {
            return;
        }
        loadComments();
    }, [slug, loadComments]);

    const openLoginModalForComments = () => {
        window.dispatchEvent(new CustomEvent("auth:open-login-modal", {
            detail: {
                nextPath: `/blog/${slug}`,
                title: "Войдите, чтобы оставить комментарий",
            },
        }));
    };

    const submitComment = async (event) => {
        event.preventDefault();
        const text = commentText.trim();
        if (!text) {
            setCommentError("Введите текст комментария.");
            return;
        }

        setCommentSubmitting(true);
        setCommentError("");
        try {
            await axios.post(
                `${API_BASE_URL}/api/articles/${slug}/comments`,
                { comment: text },
                { withCredentials: true },
            );
            setCommentText("");
            showNotification({
                type: "success",
                text: "Комментарий отправлен. После проверки модератором он появится на странице.",
            });
        } catch (e) {
            setCommentError(e?.response?.data?.error || "Не удалось отправить комментарий.");
        } finally {
            setCommentSubmitting(false);
        }
    };

    const toggleLike = async () => {
        if (!article) {
            return;
        }

        if (!isAuthenticated) {
            window.dispatchEvent(new CustomEvent("auth:open-login-modal", { detail: { nextPath: `/blog/${slug}` } }));
            return;
        }

        const wasLiked = Boolean(article.userLiked);
        setArticle((current) => current ? {
            ...current,
            userLiked: !wasLiked,
            likesCount: Math.max(0, Number(current.likesCount || 0) + (wasLiked ? -1 : 1)),
        } : current);

        try {
            if (wasLiked) {
                await axios.delete(`${API_BASE_URL}/api/likes`, { data: { entityType: "blog", entityId: Number(article.id) }, withCredentials: true });
            } else {
                await axios.post(`${API_BASE_URL}/api/likes`, { entityType: "blog", entityId: Number(article.id) }, { withCredentials: true });
            }
            setMessage("");
        } catch {
            setArticle((current) => current ? {
                ...current,
                userLiked: wasLiked,
                likesCount: Math.max(0, Number(current.likesCount || 0) + (wasLiked ? 1 : -1)),
            } : current);
            setMessage("Не удалось изменить лайк.");
        }
    };

    if (loading) {
        return (
            <div className="cms-page-root">
                <div className="cms-page-inner">
                    <article className="cms-page-panel">
                        <p className="cms-page-loading">Загрузка...</p>
                    </article>
                </div>
            </div>
        );
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    if (!article) {
        return (
            <div className="cms-page-root">
                <div className="cms-page-inner">
                    <article className="cms-page-panel">
                        <p className="cms-page-loading">{message || "Не удалось загрузить статью."}</p>
                        <Link to="/blog" className="btn-outline back-to-button">
                            Вернуться к статьям
                        </Link>
                    </article>
                </div>
            </div>
        );
    }

    const articleImage = article.image ? resolveMediaUrl(article.image) : "";
    const authorName = article.author?.name || "";

    return (
        <div className="cms-page-root">
            <div className="cms-page-inner">
                <article className="cms-page-panel blog-article-panel">
                    {!!article.image && (
                        <div className="blog-article-image-hero">
                            <img
                                className="blog-article-image"
                                src={resolveMediaUrl(article.image)}
                                alt={article.title}
                                width={1200}
                                height={675}
                                decoding="async"
                            />
                        </div>
                    )}

                    <header className="blog-article-header">
                        {!!(article.tags || []).length && (
                            <div className="blog-article-tags-top">
                                {article.tags.slice(0, 3).map((tag) => (
                                    <Link key={tag} to={`/blog?tags=${encodeURIComponent(tag)}`} className="blog-article-tag">
                                        {tag}
                                    </Link>
                                ))}
                            </div>
                        )}

                        <h1 className="blog-article-title">{article.title}</h1>

                        {article.description && (
                            <p className="blog-article-subtitle">{article.description}</p>
                        )}

                        <div className="blog-article-meta">
                            {!!article.author?.name && (
                                <div className="blog-article-author">
                                    <UserAvatar
                                        avatarUrl={article.author.avatarUrl}
                                        name={article.author.name}
                                        className="blog-article-author-avatar"
                                        size={48}
                                    />
                                    <div className="blog-article-author-info">
                                        <div className="blog-article-author-name">{article.author.name}</div>
                                        <div className="blog-article-date">
                                            {article.createdAt ? new Date(article.createdAt).toLocaleDateString('ru-RU', {
                                                year: 'numeric',
                                                month: 'long',
                                                day: 'numeric'
                                            }) : ""}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {!article.author?.name && article.createdAt && (
                                <div className="blog-article-date">
                                    {new Date(article.createdAt).toLocaleDateString('ru-RU', {
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </div>
                            )}
                        </div>
                    </header>

                    {!!message && <p className="form-message">{message}</p>}

                    <div className="blog-article-content">
                        <div className="rich-content cms-page-content" dangerouslySetInnerHTML={{ __html: article.content || "" }} />
                    </div>

                    <section className="blog-article-comments">
                        <h2 className="blog-article-comments__title">Комментарии</h2>

                        {isAuthenticated ? (
                            <form className="blog-article-comment-form" onSubmit={submitComment}>
                                <label className="blog-article-comment-form__label" htmlFor="blog-comment-text">
                                    Ваш комментарий
                                </label>
                                <textarea
                                    id="blog-comment-text"
                                    className="ui-textarea"
                                    rows={4}
                                    value={commentText}
                                    onChange={(e) => {
                                        setCommentText(e.target.value);
                                        if (commentError) setCommentError("");
                                    }}
                                    disabled={commentSubmitting}
                                    placeholder="Напишите комментарий..."
                                />
                                <div className="blog-article-comment-form__actions">
                                    <button type="submit" className="toolbar-btn" disabled={commentSubmitting}>
                                        {commentSubmitting ? "Отправка..." : "Добавить"}
                                    </button>
                                </div>
                                <p className="entity-popup__muted">Комментарий появится на сайте после проверки модератором.</p>
                                {!!commentError && <p className="form-message">{commentError}</p>}
                            </form>
                        ) : (
                            <p className="entity-popup__muted">
                                <button type="button" className="inline-link" onClick={openLoginModalForComments}>
                                    Войдите
                                </button>
                                , чтобы оставить комментарий.
                            </p>
                        )}

                        {commentsLoading && <p className="page-status-message">Загрузка комментариев...</p>}
                        {!commentsLoading && !comments.length && (
                            <p className="page-status-message">Комментариев пока нет.</p>
                        )}
                        {!!comments.length && (
                            <div className="comments-list blog-article-comments__list">
                                {comments.map((item) => (
                                    <BlogCommentItem key={item.id} comment={item} />
                                ))}
                            </div>
                        )}
                    </section>

                    <footer className="blog-article-footer">
                        {!!(article.tags || []).length && (
                            <div className="blog-article-tags-section">
                                <h3 className="blog-article-tags-title">Теги статьи</h3>
                                <div className="blog-tags">
                                    {article.tags.map((tag) => (
                                        <Link key={tag} to={`/blog?tags=${encodeURIComponent(tag)}`}>
                                            #{tag}
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="blog-article-actions">
                            {isAuthenticated && (
                                <button
                                    type="button"
                                    className={article.userLiked ? "btn-liked blog-article-like-btn" : "btn-like blog-article-like-btn"}
                                    onClick={toggleLike}
                                    aria-label={article.userLiked ? "Убрать лайк" : "Поставить лайк"}
                                >
                                    <span aria-hidden className="blog-like-btn" style={{ fontSize: "1rem", lineHeight: 1, opacity: article.userLiked ? 1 : 0.45 }}>♥</span>
                                    <span>{article.likesCount ?? 0}</span>
                                </button>
                            )}
                            {!isAuthenticated && (
                                <span className="article-item-muted">
                                    <span aria-hidden>♡</span>
                                    <span>{article.likesCount ?? 0}</span>
                                </span>
                            )}
                            <Link to="/blog" className="btn-secondary">
                                Читать ещё
                            </Link>
                        </div>
                    </footer>
                </article>
            </div>
        </div>
    );
}
